import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import { useCallback, useEffect, useRef, useState } from "react";
import { GlobalState } from "context/GlobalContext";
import axios from "axios";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import moment from "moment";
import EditFactoryDetails from "../FactoryDetails/EditFactoryDetails/EditFactoryDetails";
import {
	EditCompanyDetailsModalTypes,
	factoryUrlParamName,
	foreignKeys,
	supplierDrawerElements,
	supplierUrlParamName,
} from "data/supplierPageData";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SupplierPageHeader, supplierUi } from "../../shared/supplierUi";
import TanstackDatagrid from "components/ReusableComponents/TanstackDatagrid/TanstackDatagrid";

const factoryTablecolumns = [
	{
		key: "factory_code",
		name: "Factory Code",
		align: "center",
		headerAlign: "center",
		width: 140,
	},
	{
		key: "name",
		name: "Name",
		align: "center",
		headerAlign: "center",
		width: 180,
		flex: 1,
		minWidth: 160,
	},
	{
		key: "factory_liscence_no",
		name: "Licence No",
		align: "center",
		headerAlign: "center",
		width: 160,
	},
	{
		key: "factory_liscence_expiry_date",
		name: "Licence Expiry",
		align: "center",
		headerAlign: "center",
		width: 140,
		renderCell: ({ row }) => moment(row.factory_liscence_expiry_date).format("DD-MM-YYYY"),
		valueGetter: ({ row }) =>
			row.factory_liscence_expiry_date ? moment(row.factory_liscence_expiry_date).format("DD-MM-YYYY") : "",
	},
	{ key: "factory_city", headerAlign: "center", align: "center", name: "City", width: 130 },
	{ key: "factory_state", headerAlign: "center", align: "center", name: "State", width: 130 },
	{
		key: "factory_gst_no",
		headerAlign: "center",
		align: "center",
		name: "GST No",
		width: 140,
	},
	{
		key: "factory_pincode",
		headerAlign: "center",
		align: "center",
		name: "Pincode",
		width: 110,
	},
];

const filterRows = (rows, filters) => {
	let next = rows || [];
	Object.entries(filters || {}).forEach(([key, value]) => {
		if (value == null || String(value).trim() === "") return;
		const q = String(value).toLowerCase();
		const col = factoryTablecolumns.find((c) => c.key === key);
		next = next.filter((row) => {
			const raw = row?.[key];
			const display =
				typeof col?.valueGetter === "function"
					? String(col.valueGetter({ row, value: raw, column: col, field: key }) ?? "")
					: String(raw ?? "");
			return display.toLowerCase().includes(q);
		});
	});
	return next;
};

const AllFactories = ({
	factories,
	setFactories,
	selectedFactory,
	setSelectedFactory,
	companyData,
	setTab,
	disableEdit,
	hidePageHeader = false,
}) => {
	const [modalOpen, setModalOpen] = useState(false);
	const [pageData, setPageData] = useState([]);
	const [rowCount, setRowCount] = useState(0);
	const [filterObj, setFilterObj] = useState({});
	const [gridKey, setGridKey] = useState(0);
	const sourceRef = useRef(factories || []);
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();

	useEffect(() => {
		sourceRef.current = factories || [];
		setGridKey((k) => k + 1);
	}, [factories]);

	const getServerSideData = useCallback(async (limit, offset, filters) => {
		const filtered = filterRows(sourceRef.current, filters);
		return {
			rows: filtered.slice(offset, offset + limit),
			total_rows_count: filtered.length,
		};
	}, []);

	const goToFactoryTab = (factoryId) => {
		const params = new URLSearchParams(searchParams);
		params.set(factoryUrlParamName, factoryId);
		params.delete(supplierUrlParamName);
		const qs = params.toString();
		navigate(`../${supplierDrawerElements.factory.key}${qs ? `?${qs}` : ""}`);
	};

	const handleGridRowClick = async ({ row }) => {
		setLoadingOpen(true);
		try {
			const factory = await axios.get(`/api/supplier/factory?id=${row.id}`);
			setSelectedFactory({ ...factory.data.result });
			goToFactoryTab(row.id);
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Unable To Get factory details",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} finally {
			setLoadingOpen(false);
		}
	};

	const createFactory = async (body, setModalOpenFn) => {
		const obj = {};
		obj[foreignKeys.supplier] = companyData.id;
		setLoadingOpen(true);

		try {
			let res = await axios.post(`/api/supplier/create_factory`, { ...body, ...obj });
			setSelectedFactory(res.data.result);
			setFactories([...(factories || []), res.data.result]);
			goToFactoryTab(res.data.result.id);
			setLoadingOpen(false);
			setModalOpenFn(false);
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Created",
				severity: "success",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} catch (error) {
			setLoadingOpen(false);

			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Unable To Update Details",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		}
	};

	// Internal: fixed viewport calc (shell header sits above).
	// External: fill flex parent from SupplierUser shell.
	const sectionSx = hidePageHeader
		? { height: "calc(100vh - 200px)", minHeight: 420 }
		: { flex: 1, minHeight: 0, height: 0 };

	const newFactoryBtn = !disableEdit ? (
		<Button
			variant="contained"
			size="small"
			startIcon={<AddRoundedIcon />}
			onClick={() => setModalOpen(true)}
			sx={{
				textTransform: "none",
				fontWeight: 600,
				borderRadius: 1.5,
				px: 1.75,
				boxShadow: "none",
				"&:hover": { boxShadow: "none" },
			}}>
			New Factory
		</Button>
	) : null;

	return (
		<Stack width="100%" spacing={2} sx={sectionSx}>
			{!hidePageHeader ? (
				<SupplierPageHeader
					title="Factories"
					subtitle="Select a factory to view details, documents, and contacts"
					sx={{ mb: 0, flexShrink: 0 }}
					actions={newFactoryBtn}
				/>
			) : null}
			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					height: 0,
					width: "100%",
					border: `1px solid ${supplierUi.border}`,
					borderRadius: supplierUi.radius,
					bgcolor: "#fff",
					overflow: "hidden",
					display: "flex",
					flexDirection: "column",
					"& > .MuiPaper-root": {
						flex: 1,
						minHeight: 0,
						height: "100% !important",
					},
				}}>
				<TanstackDatagrid
					key={gridKey}
					columns={factoryTablecolumns}
					pageData={pageData}
					setPageData={setPageData}
					rowCount={rowCount}
					setRowCount={setRowCount}
					filterObj={filterObj}
					setFilterObj={setFilterObj}
					getServerSideData={getServerSideData}
					rowsPerPage={10}
					emptyMessage="No factories found"
					handleRowClick={handleGridRowClick}
					getRowId={(row) => row.id}
					headerRowStyle={{ background: "#FAFAFA" }}
					rowHeight={48}
					headerHeight={48}>
					{hidePageHeader ? <Box display="flex" justifyContent="flex-end">{newFactoryBtn}</Box> : null}
				</TanstackDatagrid>
			</Box>

			<EditFactoryDetails
				modalOpen={modalOpen}
				setModalOpen={setModalOpen}
				basicDetails={selectedFactory}
				setBasicDetails={setSelectedFactory}
				type={EditCompanyDetailsModalTypes.onbarding}
				submitFunction={createFactory}
			/>
		</Stack>
	);
};

export default AllFactories;
